import { buildDateTime } from "utils/dateHelper";
import { absTimeDifference } from "utils/timeDifference";
import moment from "moment";
import { VscPassFilled } from "react-icons/vsc";
import { MdPendingActions } from "react-icons/md";
import { TiTick } from "react-icons/ti";
import { TiDelete } from "react-icons/ti";
import { leaveTypes } from "pages/StaffApplication/constants";
import { FiX } from "react-icons/fi";

export const eventTypes = {
	ATTENDANCE: "Attendance",
	STAFF_APPLICATION: "Staff Application",
};

export const colorMap = {
	COMPLETED: { name: "green", code: "#2dce89" },
	HALF_COMPLETED: { name: "red", code: "#ffd600" },
	UNATTENDED: { name: "default" },
};

export const colorLogic = (event) => {
	if (event.arrivalTime && event.leavingTime) return colorMap.COMPLETED.name;
	if (event.arrivalTime) return colorMap.HALF_COMPLETED.name;
	return "default";
};

export const eventColors = (event, start, end, isSelected) => {
	return { className: `event-${colorLogic(event)}` };
};

export const transformAttendance = (events) =>
	events.map((event) => {
		const start = buildDateTime(event.date, String(event.arrivalTime).slice(0, 8));
		const end = event.leavingTime ? buildDateTime(event.date, String(event.leavingTime).slice(0, 8)) : start;
		return {
			...event,
			start,
			end,
			title: event?.arrivalTime && event?.leavingTime ? "Tapped In & Out" : "Tapped In",
			eventType: eventTypes.ATTENDANCE,
		};
	});

export const transformLeaves = (events) =>
	events.map((event) => {
		const eventDate = buildDateTime(event.date);
		return {
			...event,
			start: eventDate,
			end: eventDate,
			title: `${event.isOt ? "OT" : event.type} (${
				event.isOt
					? (absTimeDifference(event.startTime, event.endTime) / 60).toFixed(2) + "h"
					: event.period === "HD"
					? event.amPm
					: event.period
			}) ${event.deleted ? "Deleted" : ""} ${event.approved ? "[Approved]" : "[Pending]"}`,
			eventType: eventTypes.STAFF_APPLICATION,
		};
	});

export const mergeEvents = (attendance, leaves, ot) => (attendance && leaves && ot && [...attendance, ...leaves, ...ot]) || [];

export const leaveTableColumns = [
	{
		id: "status",
		Header: "Processed",
		accessor: (row) => (
			<div className={`${row.status === "Processed" ? "text-success" : "text-warning"}`}>
				{row.status === "Processed" ? <VscPassFilled size={20} /> : <MdPendingActions size={20} />}
			</div>
		),
		sortable: true,
		filterable: true,
	},
	{
		id: "date",
		Header: "Date",
		accessor: (row) => new Date(row.date).toDateString(),
		sortable: true,
		filterable: true,
	},
	{
		id: "leaveType",
		Header: "leaveType",
		accessor: "type",
		filter: (rows, columnIds, filterValue) => {
			if (filterValue === "all") return rows;
			return rows.filter((row) => row.values.leaveType === filterValue);
		},
		Filter: ({ setFilter, column }) => (
			<select className="form-control" onChange={(event) => setFilter(column.id, event.target.value)} onClick={(e) => e.stopPropagation()}>
				<option value="all">All</option>
				{leaveTypes.map(({ value, label }) => (
					<option value={value} key={value}>
						{label}
					</option>
				))}
			</select>
		),
		filterable: true,
	},
	{
		id: "approved",
		Header: "Approved",
		accessor: "approved",
		Cell: ({ value }) => (
			<div className={`${value ? "text-success" : "text-danger"}`}>{value ? <TiTick size={20} /> : <TiDelete size={20} />}</div>
		),
		filter: (rows, columnIds, filterValue) => {
			if (filterValue === "all") return rows;
			return rows.filter((row) => JSON.stringify(row.values.approved) === filterValue);
		},
		Filter: ({ setFilter, column }) => (
			<select className="form-control" onChange={(event) => setFilter(column.id, event.target.value)} onClick={(e) => e.stopPropagation()}>
				<option value="all">All</option>
				<option value="true">Approved</option>
				<option value="false">Not Approved</option>
			</select>
		),
		filterable: true,
	},
	{
		id: "duration",
		Header: "Duration",
		accessor: (row) => {
			if (row.period === "HD") return "4.00h";
			if (row.period === "FD") return "8.00h";
			return `${Math.min(8, absTimeDifference(row.startTime, row.endTime) / 60).toFixed(2)}h`;
		},
		sortable: true,
		filterable: true,
	},
];

export const otTableColumns = [
	{
		id: "status",
		Header: "Status",
		accessor: (row) => (
			<div className={`${row.status === "Processed" ? "text-success" : "text-warning"}`}>
				{row.status === "Processed" ? <VscPassFilled size={20} /> : <MdPendingActions size={20} />}
			</div>
		),
		sortable: true,
		filterable: true,
	},
	{
		id: "date",
		Header: "Date",
		accessor: (row) => new Date(row.date).toDateString(),
		sortable: true,
		filterable: true,
	},
	{
		id: "duration",
		Header: "Duration",
		accessor: (row) => {
			if (row.period === "HD") return "4:00h";
			if (row.period === "FD") return "8:00h";
			return `${Math.min(8, absTimeDifference(row.startTime, row.endTime) / 60).toFixed(2)}h`;
		},
		sortable: true,
		filterable: true,
	},
	{
		id: "approved",
		Header: "Approved",
		accessor: (row) => (
			<div className={`${row.approved ? "text-success" : "text-danger"}`}>{row.approved ? <TiTick size={20} /> : <TiDelete size={20} />}</div>
		),
		sortable: true,
		filterable: true,
	},
];

export const myLessonRecordsTableColumn = [
	{
		id: "lessonDate",
		Header: "Lesson Date",
		accessor: (row) => row.date,
		sortable: true,
		filterable: true,
	},
	{
		id: "lessonCentre",
		Header: "Centre",
		accessor: (row) => row.centre,
		sortable: true,
		filterable: true,
	},
	{
		id: "lessonStartTime",
		Header: "Lesson Start Time",
		accessor: (row) => row.startTime,
		sortable: true,
		filterable: true,
	},
	{
		id: "lessonEndTime",
		Header: "Lesson End Time",
		accessor: (row) => row.endTime,
		sortable: true,
		filterable: true,
	},
	{
		id: "studentName",
		Header: "Student Name",
		accessor: (row) => row?.students?.map((student) => student.studentDetail?.firstName + " " + student.studentDetail?.lastName).join(", "),
		sortable: true,
		filterable: true,
	},
	// {
	//     id: "teacherName",
	//     Header: "Teacher Name",
	//     accessor: (row) => row.teacher?.fullName,
	//     sortable: true,
	//     filterable: true,
	// },
	{
		id: "studentReportAdded",
		Header: "Student Report",
		accessor: (row) =>
			row?.students?.every((student) => student.studentReportAdded) ? <TiTick className="text-success" /> : <FiX className="text-danger" />,
		filterable: true,
	},
	{
		id: "studentReportApproved",
		Header: "Student Report Approved",
		accessor: (row) =>
			row?.students?.every((student) => student.studentReportApproved) ? <TiTick className="text-success" /> : <FiX className="text-danger" />,
		filterable: true,
	},
	// {
	//     id: "actions",
	//     Header: "actions",
	//     accessor: "actions",
	//     sortable: false,
	//     filterable: false,
	// },
];
